import { createSelector } from 'reselect'
import { entitySelector } from 'containers/Entity/selectors'
import * as R from 'ramda'
import P from 'helpers/predicates'
import { getMonthDateYearTimestampString } from 'helpers/DateUtil'

const latestRevisionId = state => state.revisions.latestRevision
const oldestRevisionId = state => state.revisions.oldestRevision
const originalRevisionId = state => state.revisions.originalRevision
const modifiedRevisionId = state => state.revisions.modifiedRevision
const revisions = state => state.revisions.revisions

const projectRevisionEntity = createSelector(
  entitySelector,
  R.compose(
    R.ifElse(
      R.isNil,
      R.always({}),
      R.identity
    ),
    R.prop('project_revisions')
  )
)

const revisionEntity = createSelector(
  entitySelector,
  R.compose(
    R.ifElse(
      R.isNil,
      R.always({}),
      R.identity
    ),
    R.prop('revisions')
  )
)

const allRevisions = createSelector(
  projectRevisionEntity,
  R.compose(
    R.map(R.prop('revision')),
    R.values
  )
)

const loadedRevisions = createSelector(
  revisions,
  R.keys
)

const getRevision = (id, revisions) => R.ifElse(
  P.isNotNil,
  R.flip(R.prop)(revisions),
  R.always({})
)(id)

const latestRevision = createSelector(
  latestRevisionId,
  revisions,
  getRevision
)

const oldestRevision = createSelector(
  oldestRevisionId,
  revisions,
  getRevision
)

const modifiedRevision = createSelector(
  modifiedRevisionId,
  revisions,
  getRevision
)

const originalRevision = createSelector(
  originalRevisionId,
  revisions,
  getRevision
)

const latestRevisionEntity = createSelector(
  latestRevisionId,
  revisionEntity,
  (id, revisions) => R.path([id, 'data'])(revisions)
)

const latestRevisionCreatedAt = createSelector(
  projectRevisionEntity,
  (projectRevisions) => {
    const projectRevisionArray = Object.values(projectRevisions)
    return projectRevisionArray.length > 0 ? 
      getMonthDateYearTimestampString(new Date(projectRevisionArray[projectRevisionArray.length - 1].createdAt)) 
      : ''
  }
)

const latestRevisionCreatedBy = createSelector(
  projectRevisionEntity,
  (projectRevisions) => {
    const projectRevisionArray = Object.values(projectRevisions)
    return projectRevisionArray.length > 0 ? projectRevisionArray[projectRevisionArray.length - 1].createdBy : ''
  }
)

const modifiedRevisionCreatedAt = createSelector(
  revisionEntity,
  modifiedRevisionId,
  (revisions, modifiedRevisionId) => {
    const modifiedRevision = revisions[modifiedRevisionId]
    return modifiedRevision ? getMonthDateYearTimestampString(new Date(modifiedRevision.createdAt)) : ''
  }
)

const modifiedRevisionCreatedBy = createSelector(
  revisionEntity,
  modifiedRevisionId,
  (revisions, modifiedRevisionId) => {
    const modifiedRevision = revisions[modifiedRevisionId]
    return modifiedRevision ? modifiedRevision.createdBy : ''
  }
)

const originalRevisionCreatedAt = createSelector(
  revisionEntity,
  originalRevisionId,
  (revisions, originalRevisionId) => {
    const originalRevision = revisions[originalRevisionId]
    return originalRevision ? getMonthDateYearTimestampString(new Date(originalRevision.createdAt)) : ''
  }
)

const originalRevisionCreatedBy = createSelector(
  revisionEntity,
  originalRevisionId,
  (revisions, originalRevisionId) => {
    const originalRevision = revisions[originalRevisionId]
    return originalRevision ? originalRevision.createdBy : ''
  }
)

const modifiedRevisionIndex = createSelector(
  projectRevisionEntity,
  modifiedRevisionId,
  (projectRevisions, modifiedRevisionId) => {
    return Object.values(projectRevisions).findIndex((projectRevision) => projectRevision.revision === modifiedRevisionId) + 1
  }
)

const originalRevisionIndex = createSelector(
  projectRevisionEntity,
  originalRevisionId,
  (projectRevisions, originalRevisionId) => {
    return Object.values(projectRevisions).findIndex((projectRevision) => projectRevision.revision === originalRevisionId) + 1
  }
)

export {
  latestRevisionId,
  oldestRevisionId,
  originalRevisionId,
  modifiedRevisionId,
  revisions,
  latestRevision,
  oldestRevision,
  modifiedRevision,
  originalRevision,
  allRevisions,
  loadedRevisions,
  latestRevisionEntity,
  latestRevisionCreatedAt,
  latestRevisionCreatedBy,
  modifiedRevisionCreatedAt,
  modifiedRevisionCreatedBy,
  originalRevisionCreatedAt,
  originalRevisionCreatedBy,
  modifiedRevisionIndex,
  originalRevisionIndex
}
